import {
  abilityTypes,
  AbilitySpell,
  AbilityType,
} from 'app/frontend/types/custom/Abilities'
import {
  getGeneralTimelineAbilities,
  getRaidPlanAbilities,
  getTimelineSpellNameById,
} from 'app/frontend/base/services/raidPlanData'
import { getAllSpecializations } from 'app/frontend/base/services/characterValues'
import { NOTE_ABILITY_ID } from '../config/generalAbilities'

export type PlanAbilityDefault = {
  message?: string
  tts_message?: string
  rgb?: string
}

export type PlanAbilityDefaults = Record<string, PlanAbilityDefault>

export const planAbilityDefaultTypes = abilityTypes.filter(
  (type): type is Exclude<AbilityType, 'assignments'> => type !== 'assignments',
)

export const getPlanAbilityDefault = (
  defaults: PlanAbilityDefaults | undefined,
  spellId?: number | null,
): PlanAbilityDefault | undefined => {
  if (spellId == null) return undefined
  return defaults?.[String(spellId)]
}

const spellNameFallback = (spellId?: number | null) => {
  if (spellId == null) return ''
  return getTimelineSpellNameById()[spellId] ?? `Spell ${spellId}`
}

export const resolveCastReminderText = ({
  message,
  spellId,
  defaults,
}: {
  message?: string
  spellId?: number | null
  defaults?: PlanAbilityDefaults
}) => {
  const trimmed = message?.trim()
  if (trimmed) return trimmed
  const defaultMessage = getPlanAbilityDefault(
    defaults,
    spellId,
  )?.message?.trim()
  if (defaultMessage) return defaultMessage
  if (spellId == null || spellId === NOTE_ABILITY_ID) return ''
  return spellNameFallback(spellId)
}

export const resolveCastTtsMessage = ({
  ttsMessage,
  spellId,
  defaults,
}: {
  ttsMessage?: string
  spellId?: number | null
  defaults?: PlanAbilityDefaults
}) => {
  const trimmed = ttsMessage?.trim()
  if (trimmed) return trimmed
  return getPlanAbilityDefault(defaults, spellId)?.tts_message?.trim() ?? ''
}

export const resolveCastRgb = ({
  rgb,
  spellId,
  defaults,
}: {
  rgb?: string
  spellId?: number | null
  defaults?: PlanAbilityDefaults
}) => {
  const trimmed = rgb?.trim()
  if (trimmed) return trimmed
  return getPlanAbilityDefault(defaults, spellId)?.rgb?.trim() ?? ''
}

export const compactPlanAbilityDefaults = (
  defaults: PlanAbilityDefaults,
): PlanAbilityDefaults => {
  return Object.entries(defaults).reduce<PlanAbilityDefaults>(
    (acc, [spellId, value]) => {
      const entry: PlanAbilityDefault = {}
      const message = value.message?.trim()
      const ttsMessage = value.tts_message?.trim()
      const rgb = value.rgb?.trim()
      if (message) entry.message = message
      if (ttsMessage) entry.tts_message = ttsMessage
      if (rgb) entry.rgb = rgb
      if (Object.keys(entry).length > 0) {
        acc[spellId] = entry
      }
      return acc
    },
    {},
  )
}

export type PlanAbilityDefaultRow = AbilitySpell & {
  classId?: number
}

export const listAbilitiesForDefaultType = (
  type: AbilityType,
): PlanAbilityDefaultRow[] => {
  const abilities = getRaidPlanAbilities()
  const specializations = getAllSpecializations()
  const seen = new Set<number>()
  const result: PlanAbilityDefaultRow[] = []
  const add = (spell: AbilitySpell, classId?: number) => {
    if (spell.type !== type) return
    if (spell.id === NOTE_ABILITY_ID) return
    if (seen.has(spell.id)) return
    seen.add(spell.id)
    result.push({ ...spell, classId })
  }
  Object.entries(abilities.classSpells).forEach(([classId, spells]) => {
    spells.forEach((spell) => add(spell, Number(classId)))
  })
  Object.entries(abilities.specSpells).forEach(([specId, spells]) => {
    const classId = specializations[specId]?.class_id ?? undefined
    spells.forEach((spell) => add(spell, classId))
  })
  getGeneralTimelineAbilities().forEach((spell) => add(spell))
  return result.sort((left, right) => left.name.localeCompare(right.name))
}
